import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Settings > Customers > Welcome message (migration 093).
//
// The message a new intake signup receives when you confirm their driveway
// size: by email, and by text as well when they agreed to texts and texting is
// on (the text is the subject line plus your rates link). GET/PUT
// /api/company/welcome; Preview is POST /api/company/welcome/preview, which
// merges the fields as they stand in the boxes (saved or not) against a sample
// customer on your first snow tier. Reset to default refills the boxes; Save
// stores it, and a box left at the default is stored as "use the default".
function WelcomeMessageCard() {
  const [loaded, setLoaded] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [subject, setSubject] = useState('');
  const [template, setTemplate] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const apply = (data) => {
    setLoaded(data);
    setEnabled(data.enabled !== false);
    setSubject(data.subject || data.defaultSubject);
    setTemplate(data.template || data.defaultTemplate);
  };

  useEffect(() => {
    let live = true;
    axios.get(`${API_URL}/api/company/welcome`)
      .then((res) => { if (live) apply(res.data); })
      .catch((err) => { if (live) setMessage({ type: 'error', text: err.response?.data?.error || 'Could not load the welcome message' }); });
    return () => { live = false; };
  }, []);

  if (!loaded) {
    return (
      <div className="card mb-lg">
        <div className="card-header"><h2 className="card-title">Welcome Message</h2></div>
        {message.text ? <div className="alert alert-error">{message.text}</div> : <p className="text-muted">Loading...</p>}
      </div>
    );
  }

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await axios.put(`${API_URL}/api/company/welcome`, { enabled, subject, template });
      apply(res.data);
      setMessage({ type: 'success', text: 'Welcome message saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Could not save the welcome message' });
    } finally {
      setSaving(false);
    }
  };

  const showPreview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API_URL}/api/company/welcome/preview`, { subject, template });
      setPreview(res.data);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Could not build the preview' });
    } finally {
      setPreviewing(false);
    }
  };

  const resetToDefault = () => {
    setSubject(loaded.defaultSubject);
    setTemplate(loaded.defaultTemplate);
    setPreview(null);
  };

  const atDefault = subject.trim() === loaded.defaultSubject && template.trim() === loaded.defaultTemplate.trim();

  return (
    <div className="card mb-lg">
      <div className="card-header">
        <h2 className="card-title">Welcome Message</h2>
        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
          Sent once to a new signup from your intake form when you confirm their driveway size
        </p>
      </div>

      <form onSubmit={save}>
        {message.text && <div className={`alert alert-${message.type}`} aria-live="polite">{message.text}</div>}

        <div className="form-group">
          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              style={{ marginTop: '3px' }}
              data-testid="welcome-enabled"
            />
            <div>
              <span style={{ fontWeight: 500 }}>Send a welcome message</span>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px', marginBottom: 0 }}>
                Sent by email, with replies going to your business email. If your texting is set up and active,
                customers who agreed to texts also get a short text: the subject line and a link to your rates
                page.
              </p>
            </div>
          </label>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="welcomeSubject">Subject</label>
          <input
            id="welcomeSubject"
            type="text"
            className="form-input"
            value={subject}
            maxLength={120}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="welcomeTemplate">Message</label>
          <textarea
            id="welcomeTemplate"
            className="form-textarea"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            rows={14}
          />
          <small className="text-muted" data-testid="welcome-fields">
            Fields you can use: {loaded.fields.map((f) => `{{${f}}}`).join(', ')}
          </small>
        </div>

        {preview && (
          <div className="disclaimer-preview" data-testid="welcome-preview">
            <label className="form-label">Preview (a sample customer)</label>
            <div className="disclaimer-preview-box">
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>{preview.subject}</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{preview.body}</div>
            </div>
            <small className="text-muted">Text version: {preview.sms}</small>
          </div>
        )}

        <div className="disclaimer-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Welcome Message'}
          </button>
          <button type="button" className="btn btn-outline" onClick={showPreview} disabled={previewing}>
            {previewing ? 'Building...' : 'Preview'}
          </button>
          <button type="button" className="btn btn-outline" onClick={resetToDefault} disabled={atDefault}>
            Reset to default
          </button>
        </div>
      </form>
    </div>
  );
}

export default WelcomeMessageCard;
